/**
 * PROTOTYPE/DEMO ONLY. Fabricated figures for the Performa Bisnis mockup — the
 * backend does not yet expose revenue or time-series aggregation.
 *
 * shortcut: dummy prototype data. When the real metrics endpoint ships, delete
 * this file and point `api.ts` at the live request. Nothing else invents
 * numbers, so removing it leaves the empty state in place.
 */
import type { PerformanceDataset, PerformancePeriod } from './schema'

const weeklyLabels = ['Jum 23', 'Sab 24', 'Min 25', 'Sen 26', 'Sel 27', 'Rab 28', 'Kam 29']

const mockDataset: Record<PerformancePeriod, PerformanceDataset> = {
  daily: {
    rangeLabel: 'Hari ini · 30 Okt 2026',
    metrics: [
      {
        id: 'revenue',
        label: 'Penjualan',
        display: 'Rp1.850.000',
        delta: '+3,2%',
        deltaPositive: true,
        chartTitle: 'Penjualan per jam',
        currency: true,
        series: [
          { label: '08.00', value: 120_000 },
          { label: '10.00', value: 280_000 },
          { label: '12.00', value: 410_000 },
          { label: '14.00', value: 360_000 },
          { label: '16.00', value: 300_000 },
          { label: '18.00', value: 380_000 },
        ],
      },
      {
        id: 'orders',
        label: 'Pesanan',
        display: '32',
        delta: '+4,1%',
        deltaPositive: true,
        chartTitle: 'Pesanan per jam',
        currency: false,
        series: [
          { label: '08.00', value: 2 },
          { label: '10.00', value: 5 },
          { label: '12.00', value: 8 },
          { label: '14.00', value: 6 },
          { label: '16.00', value: 5 },
          { label: '18.00', value: 6 },
        ],
      },
      {
        id: 'aov',
        label: 'Penjualan per Pesanan',
        display: 'Rp57.812',
        delta: '+1,1%',
        deltaPositive: true,
        chartTitle: 'Rata-rata nilai pesanan per jam',
        currency: true,
        series: [
          { label: '08.00', value: 60_000 },
          { label: '10.00', value: 56_000 },
          { label: '12.00', value: 51_000 },
          { label: '14.00', value: 60_000 },
          { label: '16.00', value: 60_000 },
          { label: '18.00', value: 63_000 },
        ],
      },
      {
        id: 'buyers',
        label: 'Pembeli',
        display: '28',
        delta: '+2,6%',
        deltaPositive: true,
        chartTitle: 'Pembeli unik per jam',
        currency: false,
        series: [
          { label: '08.00', value: 2 },
          { label: '10.00', value: 4 },
          { label: '12.00', value: 7 },
          { label: '14.00', value: 5 },
          { label: '16.00', value: 4 },
          { label: '18.00', value: 6 },
        ],
      },
    ],
  },
  weekly: {
    rangeLabel: '7 hari terakhir · 23–29 Okt 2026',
    metrics: [
      {
        id: 'revenue',
        label: 'Penjualan',
        display: 'Rp12.480.000',
        delta: '+9,7%',
        deltaPositive: true,
        chartTitle: 'Penjualan per hari',
        currency: true,
        series: [
          { label: weeklyLabels[0]!, value: 1_420_000 },
          { label: weeklyLabels[1]!, value: 1_680_000 },
          { label: weeklyLabels[2]!, value: 1_540_000 },
          { label: weeklyLabels[3]!, value: 1_920_000 },
          { label: weeklyLabels[4]!, value: 2_230_000 },
          { label: weeklyLabels[5]!, value: 2_050_000 },
          { label: weeklyLabels[6]!, value: 1_640_000 },
        ],
      },
      {
        id: 'orders',
        label: 'Pesanan',
        display: '214',
        delta: '+7,5%',
        deltaPositive: true,
        chartTitle: 'Pesanan per hari',
        currency: false,
        series: [
          { label: weeklyLabels[0]!, value: 24 },
          { label: weeklyLabels[1]!, value: 29 },
          { label: weeklyLabels[2]!, value: 27 },
          { label: weeklyLabels[3]!, value: 33 },
          { label: weeklyLabels[4]!, value: 38 },
          { label: weeklyLabels[5]!, value: 34 },
          { label: weeklyLabels[6]!, value: 29 },
        ],
      },
      {
        id: 'aov',
        label: 'Penjualan per Pesanan',
        display: 'Rp58.318',
        delta: '+2,0%',
        deltaPositive: true,
        chartTitle: 'Rata-rata nilai pesanan per hari',
        currency: true,
        series: [
          { label: weeklyLabels[0]!, value: 59_000 },
          { label: weeklyLabels[1]!, value: 58_000 },
          { label: weeklyLabels[2]!, value: 57_000 },
          { label: weeklyLabels[3]!, value: 58_200 },
          { label: weeklyLabels[4]!, value: 58_700 },
          { label: weeklyLabels[5]!, value: 60_300 },
          { label: weeklyLabels[6]!, value: 56_600 },
        ],
      },
      {
        id: 'buyers',
        label: 'Pembeli',
        display: '142',
        delta: '+5,2%',
        deltaPositive: true,
        chartTitle: 'Pembeli unik per hari',
        currency: false,
        series: [
          { label: weeklyLabels[0]!, value: 18 },
          { label: weeklyLabels[1]!, value: 21 },
          { label: weeklyLabels[2]!, value: 19 },
          { label: weeklyLabels[3]!, value: 23 },
          { label: weeklyLabels[4]!, value: 26 },
          { label: weeklyLabels[5]!, value: 22 },
          { label: weeklyLabels[6]!, value: 20 },
        ],
      },
    ],
  },
  monthly: {
    rangeLabel: '6 bulan terakhir · Mei–Okt 2026',
    metrics: [
      {
        id: 'revenue',
        label: 'Penjualan',
        display: 'Rp68.900.000',
        delta: '+12,4%',
        deltaPositive: true,
        chartTitle: 'Penjualan per bulan',
        currency: true,
        series: [
          { label: 'Mei', value: 8_900_000 },
          { label: 'Jun', value: 9_600_000 },
          { label: 'Jul', value: 10_200_000 },
          { label: 'Agu', value: 11_400_000 },
          { label: 'Sep', value: 13_300_000 },
          { label: 'Okt', value: 15_500_000 },
        ],
      },
      {
        id: 'orders',
        label: 'Pesanan',
        display: '1.184',
        delta: '+8,9%',
        deltaPositive: true,
        chartTitle: 'Pesanan per bulan',
        currency: false,
        series: [
          { label: 'Mei', value: 156 },
          { label: 'Jun', value: 168 },
          { label: 'Jul', value: 182 },
          { label: 'Agu', value: 201 },
          { label: 'Sep', value: 228 },
          { label: 'Okt', value: 249 },
        ],
      },
      {
        id: 'aov',
        label: 'Penjualan per Pesanan',
        display: 'Rp58.192',
        delta: '+3,2%',
        deltaPositive: true,
        chartTitle: 'Rata-rata nilai pesanan per bulan',
        currency: true,
        series: [
          { label: 'Mei', value: 57_000 },
          { label: 'Jun', value: 57_100 },
          { label: 'Jul', value: 56_000 },
          { label: 'Agu', value: 56_700 },
          { label: 'Sep', value: 58_300 },
          { label: 'Okt', value: 62_200 },
        ],
      },
      {
        id: 'buyers',
        label: 'Pembeli',
        display: '792',
        delta: '+6,8%',
        deltaPositive: true,
        chartTitle: 'Pembeli unik per bulan',
        currency: false,
        series: [
          { label: 'Mei', value: 108 },
          { label: 'Jun', value: 116 },
          { label: 'Jul', value: 124 },
          { label: 'Agu', value: 138 },
          { label: 'Sep', value: 152 },
          { label: 'Okt', value: 164 },
        ],
      },
    ],
  },
}

/** Returns the fabricated dataset for a period. Pure, so it is safe in tests. */
export function performanceMockDataset(period: PerformancePeriod): PerformanceDataset {
  return mockDataset[period]
}
